"use client";

import * as RadixTooltip from "@radix-ui/react-tooltip";
import { ReactNode, useState } from "react";
import { cn } from "./lib/utils";

interface TooltipProps {
	text?: React.ReactNode | null;
	component?: ReactNode;
	className?: string;
	children?: React.ReactNode;
	delayDuration?: number;
	hideOnMouseOver?: boolean;
	hideInstantly?: boolean;
	side?: RadixTooltip.TooltipContentProps["side"];
	sideOffset?: RadixTooltip.TooltipContentProps["sideOffset"];
	alignOffset?: RadixTooltip.TooltipContentProps["alignOffset"];
}

export function Tooltip({
	children,
	className,
	text,
	component,
	delayDuration = 200,
	hideOnMouseOver = false,
	hideInstantly = false,
	side = "bottom",
	sideOffset = 6,
}: TooltipProps) {
	const [isOpen, setIsOpen] = useState(false);

	if (!text) {
		return <>{component || children}</>;
	}

	return (
		<RadixTooltip.Provider
			delayDuration={delayDuration}
			skipDelayDuration={hideInstantly ? 0 : undefined}
		>
			<RadixTooltip.Root
				open={hideInstantly ? isOpen : undefined}
				onOpenChange={hideInstantly ? setIsOpen : undefined}
			>
				<RadixTooltip.Trigger
					asChild
					onMouseEnter={hideInstantly ? () => setIsOpen(true) : undefined}
					onMouseLeave={hideInstantly ? () => setIsOpen(false) : undefined}
				>
					{children || <button type="button">+</button>}
				</RadixTooltip.Trigger>
				<RadixTooltip.Portal>
					<RadixTooltip.Content
						className={cn(
							"user-select-none z-50 max-w-[300px] select-none whitespace-pre-wrap rounded-[4px] bg-tooltip-background px-[15px] py-[10px] text-sm text-tooltip-text leading-normal shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] will-change-[transform,opacity] data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade",
							className,
							hideOnMouseOver ? "pointer-events-none" : "",
						)}
						side={side}
						sideOffset={sideOffset}
					>
						{component || text}
					</RadixTooltip.Content>
				</RadixTooltip.Portal>
			</RadixTooltip.Root>
		</RadixTooltip.Provider>
	);
}
